

































































































































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate-3';

import has from 'lodash/has';

import Typography from '@/componentsV2/atoms/Typography/Typography.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';
import NavButtonItem from '@/componentsV2/molecules/NavButtonItem/NavButtonItem.vue';
import AddressFieldDropdown from '@/componentsV2/organisms/AddressFieldDropdown/AddressFieldDropdown.vue';
import Input from '@/componentsV2/molecules/Inputs/TextField.vue';
import PhoneInput from '@/components/molecules/PhoneInput/PhoneInput.vue';
import MerchantIDTooltip from '@/components/MerchantIDTooltip/MerchantIDTooltip.vue';

import { treatError } from '@/utils';
import { getLabelInfo } from '@/utils/getLabelInfo';
import { setFocusOnFirstInput } from '@/utils/setFocusOnFirstInput';

import { AddressFieldData } from '@/models/select-dropdown';
import { ILocation } from '@/models/location';
import { IOfferListItem } from '@/models/offer';

import eventBus from '@/utils/eventBus';

import HTTPStatus from '@/constants/httpStatus';

import BillingService from '@/api/BillingService';
import LocationsService from '@/api/LocationsService';
import { EBillingStatusTypes } from '@/store/modules/businessTypes';
import SelectionBox from '@/componentsV2/molecules/Inputs/SelectionBox.vue';
import flagKeys from '@/constants/flagKeys';
import DateUtils from '@/utils/DateUtils';

export type LocationFormModalMethod = 'create' | 'edit';

export type LocationFormData = {
  address: AddressFieldData | null;
  aditionalAddress: string;
  locationName: string;
  merchantId: string;
  phone: {
    code: string;
    iso: string;
    number: string;
  };
  billingAcceptance: boolean;
};

type TriggerPosition = 'header' | 'footer';

@Component({
  components: {
    ValidationObserver,
    ValidationProvider,
    AddressFieldDropdown,
    Input,
    PhoneInput,
    FilledButton,
    MerchantIDTooltip,
    Typography,
    NavButtonItem,
    SelectionBox
  }
})
export default class AddLocation extends Vue {
  @Prop() method!: LocationFormModalMethod;
  @Prop() location?: ILocation;
  @Prop() offers!: IOfferListItem[];
  @Prop() reloadLocations!: () => void;
  @Prop() onClick?: () => void;
  @Prop() modal?: boolean;

  observer: IntersectionObserver | null = null;
  triggerVisibility: Record<TriggerPosition, boolean> = {
    header: false,
    footer: false
  };

  billingService = new BillingService();
  locationService = new LocationsService({ disableToastMessage: true });
  loading = false;

  mobileModalOpened = false;

  hasTheSameLocationName = false;

  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  formData: LocationFormData = {
    address: null,
    aditionalAddress: '',
    locationName: '',
    merchantId: '',
    phone: {
      code: this.selectedCountry.phoneCode,
      iso: this.selectedCountry.countryIso2,
      number: ''
    },
    billingAcceptance: false
  };

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get fullAddress() {
    return getLabelInfo(
      ['address', 'city', 'state', 'zip', 'countryName'],
      this.location || {}
    );
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get billingModuleV3() {
    return this.flags.includes(flagKeys.billingModuleV3);
  }

  get delayedPeriod() {
    return (
      this.billingModuleV3 &&
      this.billingStatus === EBillingStatusTypes.DELAY_PERIOD
    );
  }

  get acceptanceBoxTerms() {
    const date = DateUtils.formattedDate({
      date: this.billingSubscription.nextPaymentAt
    });

    if (this.delayedPeriod)
      return `I authorize Twism to update my current plan and mint 25 additional Coins per week for this location starting on ${date}.`;

    return 'I authorize Twism to update my current plan to include 25 Coins minted per week to Twism for this new location.';
  }

  get subscribedBilling() {
    return this.billingStatus === EBillingStatusTypes.SUBSCRIBED;
  }

  get billingStatus() {
    return this.businessInfo.subscriptionStatus;
  }

  get countryName() {
    return this.$store.getters['internationalization/getCountrySettings'].name;
  }

  get billingSubscription() {
    return this.$store.getters['business/getBillingSubscription'] || {};
  }

  get selectedCountry() {
    return this.$store.getters['businessOnboarding/getSelectedCountry'];
  }

  get initialAddressValue() {
    if (!this.location || !this.location.id) {
      return { value: null, label: null };
    }

    if (!this.location.countryName) {
      (
        this.formData.address || ({} as AddressFieldData)
      ).countryName = this.countryName;
    }

    const fullAddress = this.fullAddress;

    return {
      value: fullAddress,
      label: fullAddress,
      additionalInformation: {
        address: this.location.address,
        cityName: this.location.city,
        countryCode: this.location.phoneCountryIso,
        countryName: this.countryName,
        fullAddress: fullAddress,
        postCode: this.location.zip,
        stateCode: this.location.state,
        stateName: this.location.stateName
      }
    };
  }

  @Watch('loading')
  changedLoading() {
    this.$emit('changedLoading', this.loading);
  }

  onClickSubmit() {
    if (this.onClick) {
      this.onClick();
      return;
    }

    this.onSubmit();
  }

  mounted() {
    this.handleTriggerObserver();
    eventBus.$on('locationFormSetErrors', this.handleSetFormErrors);
    eventBus.$on('createLocation', this.onSubmit);
    this.fillDefaultFormData();
    this.$emit('input', this.formData);
  }

  beforeDestroy() {
    eventBus.$off('locationFormSetErrors', this.handleSetFormErrors);
    eventBus.$off('createLocation', this.onSubmit);
  }

  onClose() {
    eventBus.$emit('closeModal');
  }

  @Watch('formData.address')
  changedFormDataAddress() {
    this.$emit('input', this.formData);
  }

  @Watch('formData.locationName')
  changedFormDataLocationName() {
    this.$emit('input', this.formData);
  }

  handleSetFormErrors(errors: Record<string, string | string[]>) {
    const formRef = this.$refs.observer;

    formRef.setErrors(errors);
  }

  fillDefaultFormData() {
    if (!this.location) return;

    const {
      addressAdditional,
      address,
      zip,
      city,
      state,
      stateName = '',
      phoneCountryIso = '',
      phoneCountryCode = '',
      phone = '',
      merchantId = '',
      name = ''
    } = this.location;

    this.formData = {
      ...this.formData,
      aditionalAddress: addressAdditional,
      locationName: name,
      merchantId,
      phone: {
        iso: phoneCountryIso,
        code: phoneCountryCode,
        number: phone
      },
      address: {
        address,
        fullAddress: this.fullAddress,
        postCode: zip,
        cityName: city,
        stateName,
        stateCode: state,
        countryName: this.countryName,
        countryCode: phoneCountryIso
      }
    };
  }

  handleClearAddress() {
    const locationNameInputEl = document.querySelector<HTMLInputElement>(
      'input[name="locationName"]'
    );

    if (locationNameInputEl) {
      locationNameInputEl.value = '';
    }

    this.formData = Object.assign({}, this.formData, {
      address: null,
      locationName: ''
    });
  }

  handleTriggerObserver() {
    this.observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const position = entry.target.getAttribute(
            'data-position'
          ) as TriggerPosition;

          this.triggerVisibility[position] =
            !entry.isIntersecting && !!position;
        }
      },
      {
        root: document.querySelector('.add-location-step'),
        rootMargin: '24px',
        threshold: 0
      }
    );

    const targets = document.querySelectorAll<HTMLElement>(
      '.add-location-step__body-trigger'
    );

    for (const target of targets) {
      this.observer.observe(target);
    }
  }

  async handleAddressChange(addressData: any) {
    const { status, data } = await this.locationService.getSuggestionName({
      businessId: this.businessInfo.id,
      address: addressData.address,
      city: addressData.cityName,
      state: addressData.stateCode,
      country: addressData.countryCode
    });

    if (status !== HTTPStatus.OK) return;

    const locationNameInputEl = document.querySelector<HTMLInputElement>(
      'input[name="locationName"]'
    );

    if (locationNameInputEl) {
      locationNameInputEl.value = data.suggestionName;
    }

    this.formData = Object.assign({}, this.formData, {
      locationName: data.suggestionName
    });
  }

  handleCloseMobileModal() {
    this.mobileModalOpened = false;
  }

  handleTippyClick() {
    if (!this.$mobileCheck()) return;

    this.mobileModalOpened = true;
  }

  handleCloseTooltip() {
    const triggerEl = document.querySelector('[data-tippy="merchant-tooltip"]');

    if (triggerEl) {
      triggerEl.dispatchEvent(new Event('click'));
    }
  }

  handleMID() {
    location.href = 'mailto:hello@twism.com';
  }

  async updatedBusinessLocations() {
    const { status, data } = await this.locationService.getLocations();

    if (status === HTTPStatus.OK) {
      this.$store.dispatch('business/SET_BUSINESS_INFO', {
        ...this.businessInfo,
        businessLocations: data
      });
    }
  }

  formatPayload(formData: LocationFormData) {
    const {
      locationName,
      aditionalAddress,
      merchantId,
      address,
      phone
    } = formData;

    const payload = {
      ...(address && {
        address: address.address,
        city: address.cityName,
        zip: address.postCode,
        state: address.stateCode,
        addressAdditional: aditionalAddress,
        locationName,
        fullAddress: address.fullAddress
      }),
      phone: phone.number,
      phoneCountryCode: phone.code,
      phoneCountryIso: phone.iso,
      merchantId,
      primary: this.location && this.location.primary ? true : false
    };

    return payload;
  }

  handleLocationError(errorResponse: any) {
    const errorMessage = treatError(errorResponse, false);

    if (
      (has(errorResponse, 'response.data.slug') &&
        errorResponse.response.data.slug ===
          'exceptions.business.theSameLocationName') ||
      errorMessage === 'There is another business using this address'
    ) {
      this.hasTheSameLocationName = true;
      this.$el.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    this.$notification(errorMessage, 'error');
  }

  async handleCreateLocation(onSuccess?: () => void, showNotification = true) {
    const payload = this.formatPayload(this.formData);

    this.loading = true;
    const locationResponse = await this.locationService.create(payload);

    let subscription = false;

    if (this.subscribedBilling) {
      const {
        data
      } = await this.billingService.getBillingOverviewSubscription();

      this.$store.dispatch('business/SET_BILLING_SUBSCRIPTION', data);

      subscription = data;
    }

    this.loading = false;

    if (locationResponse.status !== HTTPStatus.CREATED) {
      this.handleLocationError(locationResponse);

      return;
    }

    this.updatedBusinessLocations();
    if (showNotification && !this.modal)
      this.$notification('New location added successfully');
    this.$emit('reloadLocations');

    if (this.modal) {
      if (onSuccess) onSuccess();
      return;
    }

    if (this.offers.length || this.subscribedBilling)
      this.$emit('goToNextStep', {
        locationId: locationResponse.data.id,
        subscription
      });
    else this.onClose();
  }

  async handleEditLocation() {
    if (!this.location) return;

    const payload = this.formatPayload(this.formData);

    this.loading = true;
    const locationResponse = await this.locationService.edit(
      this.location.id,
      payload
    );
    this.loading = false;

    if (locationResponse.status !== HTTPStatus.OK) {
      this.handleLocationError(locationResponse);

      return;
    }

    if (this.location.primary) {
      this.$store.dispatch(
        'business/UPDATE_BUSINESS_PHONE',
        this.formData.phone
      );
    }

    this.updatedBusinessLocations();
    this.$notification('Location updated');
    this.$emit('reloadLocations');
    this.onClose();
  }

  async onSubmit(onSuccess?: () => void, showNotification = true) {
    const formRef = this.$refs.observer;
    const formIsValid = await formRef.validate();

    const notAccepted =
      (this.subscribedBilling || this.delayedPeriod) &&
      !this.formData.billingAcceptance &&
      this.method === 'create';
    if (!formIsValid || notAccepted) {
      if (notAccepted) {
        eventBus.$emit('locationFormSetErrors', {
          billingAcceptance: ['Accept the Coin minting update to proceed.']
        });
        if (formIsValid)
          this.$el.scrollTo({
            top: this.$el.scrollHeight,
            behavior: 'smooth'
          });
      }
      setFocusOnFirstInput('input[aria-invalid="true"]');
      return;
    }

    if (this.method === 'edit') {
      this.handleEditLocation();
      return;
    }

    this.handleCreateLocation(onSuccess, showNotification);
  }
}
